"use client";
import { useState, ChangeEvent, useEffect, useRef } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import useFilterStore from "@store/browseJobsFiltersStore";
import { useIntl } from "react-intl";
import useJobLoadingStore from "@store/isLoadingJobs";
import Checkbox from "@components/Forms/Checkbox";

type Props = {
  jobTypes: {
    id: string;
    jobType: string;
  }[];
};

const JobTypeFilter = ({ jobTypes }: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredJobTypes, setFilteredJobTypes] = useState(jobTypes);
  const [isOpen, setIsOpen] = useState(true);
  const { jobTypeFilters, addJobType, removeJobType, clearJobTypes } =
    useFilterStore();
  const { setIsLoading }: any = useJobLoadingStore();

  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState("0px");

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = jobTypes.filter((job) =>
      job.jobType.toLowerCase().includes(value)
    );
    setFilteredJobTypes(filtered);
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    if (checked) {
      setIsLoading(true);
      addJobType(value);
    } else {
      setIsLoading(true);
      removeJobType(value);
    }
  };

  const handleClear = () => {
    clearJobTypes();
    setSearchTerm("");
    setFilteredJobTypes(jobTypes);
  };

  const toggleFilterVisibility = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [isOpen]);

  const intl = useIntl();
  const translation = intl.messages;
  const jobsTranslation: any = translation["jobs"];
  const FiltersTranslation: any = jobsTranslation["filters"];

  return (
    <div className="pt-5 mt-5">
      <button
        className="flex justify-between items-center w-full py-1"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-sm lg:text-[15px] font-medium text-[#1F2223]">
          {FiltersTranslation["job-type"]}
        </span>

        <ChevronDownIcon
          className={`w-5 h-5 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Sliding animation for the filter section */}

      <div
        ref={contentRef}
        style={{ maxHeight: height }}
        className={`flex flex-col w-full overflow-hidden transition-max-height duration-500 ease-in-out`}
      >
        <div className="px-4 mt-5">
          <ul className="w-full flex flex-col gap-4">
            {filteredJobTypes.length > 0 ? (
              filteredJobTypes.map((jobType) => (
                <li key={jobType.jobType}>
                  <label className="font-medium text-sm text-[#444748] flex items-center">
                    <Checkbox
                      value={jobType.id}
                      checked={jobTypeFilters.includes(jobType.id)}
                      onChange={handleCheckboxChange}
                    />
                    {jobType.jobType.slice(0, 25) + "."}
                  </label>
                </li>
              ))
            ) : (
              <li className="text-[12px] text-gray-500">
                {FiltersTranslation["no-matching-job-types"]}
              </li>
            )}
          </ul>

          <div className="flex flex-row items-center justify-end w-full">
            <button
              onClick={handleClear}
              className="font-normal text-[12px] text-[#4DBFDA]"
            >
              {FiltersTranslation["clear"]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobTypeFilter;
